import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import { categories, animeList, articles } from '@/mocks/anime';

export default function Home() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const hotAnime = [...animeList].sort((a, b) => b.rating - a.rating).slice(0, 6);
  const latestArticles = [...articles].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 4);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <SEOHead
        title="风车动漫-专注手机动漫的门户网站"
        description="风车动漫网针对移动设备优化，作为手机动漫门户，让您通过手机便捷访问海量动漫，享受随时随地观看和分享的乐趣。提供最新动漫资讯、热门新番推荐、深度动漫评测、动漫排行盘点。"
        keywords="手机动漫,移动动漫,风车动漫,动漫门户,动漫资讯,新番推荐"
        canonicalUrl="https://fengchedongman.com/"
        type="webpage"
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Anime%20portal%20website%20hero%20background%20with%20warm%20orange%20and%20amber%20gradient%20tones%20abstract%20Japanese%20wave%20pattern%20and%20sakura%20petals%20minimalist%20and%20modern%20design%20soft%20warm%20light%20effects&width=1800&height=900&seq=hero-bg-2024-a1&orientation=landscape"
            alt="风车动漫 - 手机动漫门户"
            className="absolute inset-0 w-full h-full object-cover object-top"
            title="风车动漫 - 手机动漫门户网站"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4">
              风车动漫
            </h1>
            <p className="text-base md:text-lg text-white/90 max-w-xl mb-8 leading-relaxed">
              专注手机动漫的门户网站，随时随地畅享海量动漫资讯与精彩内容
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to={`/category/${categories[0].slug}`}
                onClick={scrollToTop}
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                浏览热门动漫
              </Link>
              <Link
                to="/about"
                onClick={scrollToTop}
                className="px-8 py-3 bg-white/15 text-white backdrop-blur-sm rounded-full font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-white/25 transition-colors border border-white/20"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Category Entrance */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">动漫分类</h2>
              <p className="text-sm text-foreground-500 mt-2">找到你最喜欢的动漫类型</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={scrollToTop}
                  className="group flex flex-col items-center p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                >
                  <span className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-200 mb-3">
                    <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                  </span>
                  <span className="font-heading text-base md:text-lg font-bold text-foreground-900 mb-1">{cat.name}</span>
                  <span className="text-xs text-foreground-400 text-center">{cat.description}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Ranking */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">热门推荐</h2>
                <p className="text-sm text-foreground-500 mt-2">评分最高的动漫作品</p>
              </div>
              <Link
                to={`/category/${categories[0].slug}`}
                onClick={scrollToTop}
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap font-medium"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5" data-product-shop>
              {hotAnime.map((anime, idx) => (
                <div key={anime.id} className="group cursor-pointer">
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                    <img
                      src={anime.coverImage}
                      alt={anime.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      title={`${anime.title} - 风车动漫推荐`}
                    />
                    <div className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs font-bold px-2 py-0.5 rounded-md">
                      {anime.rating}
                    </div>
                    {idx < 3 && (
                      <div className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-primary-500 text-background-50 text-xs font-bold rounded-full">
                        {idx + 1}
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                    {anime.title}
                  </h3>
                  <p className="text-xs text-foreground-400 mt-1">
                    {anime.episodes}集 · {anime.status}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">最新资讯</h2>
                <p className="text-sm text-foreground-500 mt-2">动漫圈最新动态与深度好文</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {latestArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  onClick={scrollToTop}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden bg-background-200">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      title={`${article.title} - 风车动漫资讯`}
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">{article.categoryName}</span>
                      <span className="text-xs text-foreground-400">{article.date}</span>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-2 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
              <div className="flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className="ri-smartphone-line text-xl"></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">移动优先</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">全面针对手机浏览优化，流畅的触控体验，让你随时随地畅享动漫资讯。</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-flashlight-line text-xl"></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">极速更新</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">专业编辑团队每日更新，第一时间带来最新动漫资讯与深度内容。</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                  <i className="ri-thumb-up-line text-xl"></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">精选推荐</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">基于真实评分的动漫推荐，帮你快速发现值得一看的优秀作品。</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}